import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import ComingSoon from "@/shared/components/ComingSoon";
import type { SidebarItem } from "./Sidebar";
import adminRoutes from "../routes/admin.routes";
import Button from "../../../shared/components/Button";
import SecondaryIcon from "@/shared/components/ui/SecondaryIcon";
import Icons from "../../../shared/constants/icon.constant";
import { isApiError } from "@/shared/api";
import { useLogout } from "@/features/auth/services";
import { useProfile } from "@/features/profile/services";

function findTitleByPath(
  items: SidebarItem[],
  pathname: string
): string | null {
  const currentLast = pathname.split("/").filter(Boolean).pop();
  for (const item of items) {
    if (item.path) {
      const itemLast = item.path.split("/").filter(Boolean).pop();
      if (itemLast && currentLast === itemLast) {
        return item.label!;
      }
    }
    if (item.children) {
      const found = findTitleByPath(item.children, pathname);
      if (found) return found;
    }
  }
  return null;
}

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [openComingSoon, setOpenComingSoon] = useState(false);
  const { mutateAsync: triggerLogout, isPending: isLoggingOut } = useLogout();
  const { data: profile } = useProfile({
    staleTime: 60_000,
    retry: 1,
  });

  const pathname = location.pathname;
  const title = findTitleByPath(adminRoutes.children, pathname);

  const handleLogout = async () => {
    try {
      await triggerLogout();
      toast.success("You have been signed out.");
      navigate("/sign-in", { replace: true });
    } catch (error) {
      if (isApiError(error) && (error.status === 401 || error.status === 403)) {
        toast.info("You have already been signed out.");
        navigate("/sign-in", { replace: true });
      } else {
        const message = isApiError(error)
          ? error.message || "Unable to log out. Please try again."
          : "Unable to log out. Please try again.";
        toast.error(message);
        return;
      }
    } finally {
      setOpen(false);
    }
  };

  return (
    <nav className="px-3 sm:px-6 w-full flex items-center justify-between gap-2">
      <h1 className="text-md">{title}</h1>
      <div className="flex items-center gap-2 sm:gap-4">
        <div onClick={() => setOpenComingSoon(true)}>
          <Button variant="icon">
            <SecondaryIcon
              icon={Icons.Notifications({ width: 16, height: 16 })}
              isActive
            />
          </Button>
        </div>
        {openComingSoon && (
          <ComingSoon onClose={() => setOpenComingSoon(false)} />
        )}

        {/* Account Dropdown */}
        <div className="relative">
          <button
            className="rounded-full flex bg-sidebar-active py-1.5 pl-2 pr-4 justify-center items-center gap-2"
            onClick={() => setOpen((prev) => !prev)}>
            <img
              src={
                profile?.profileImage ||
                "https://png.pngtree.com/png-clipart/20231019/original/pngtree-user-profile-avatar-png-image_13369988.png"
              }
              className="rounded-full bg-yellow-400 w-6 h-6 object-cover"
              alt="avatar"
            />
            <p className="mr-2 text-sm hidden sm:block">
              {profile
                ? `${profile.firstName ?? ""} ${profile.lastName ?? ""}`.trim() ||
                  profile.email
                : "My Account"}
            </p>
            <SecondaryIcon
              icon={(open ? Icons.ChevronUp : Icons.ChevronDown)({
                width: 8,
                height: 8,
              })}
              isActive
            />
          </button>

          {open && (
            <div className="absolute right-0 mt-2 w-48 bg-input-dark text-white rounded-lg shadow-lg z-50">
              <ul className="py-1">
                <li>
                  <Link
                    to="/dashboard/profile/setting"
                    className="block px-4 py-2 text-sm hover:bg-gray-700"
                    onClick={() => setOpen(false)}>
                    Profile
                  </Link>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className="w-full text-left block px-4 py-2 text-sm hover:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed">
                    {isLoggingOut ? "Logging out..." : "Logout"}
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
